import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, inr, fmtDate } from "../lib/api";
import TopBar from "../components/TopBar";
import { Button, Card, Chip, Empty, Skeleton } from "../components/ui";
import { ArrowLeft, Calendar, MapPin, Users, Receipt, PlusCircle, Trash2, Activity } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function MatchDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { club } = useAuth();
  const canManage = ["owner", "admin", "manager"].includes(club?.my_role);
  const [match, setMatch] = useState(null);
  const [members, setMembers] = useState([]);

  const load = async () => {
    try {
      const [{ data: m }, { data: mem }] = await Promise.all([
        api.get(`/matches/${id}`),
        api.get("/members"),
      ]);
      setMatch(m); setMembers(mem);
    } catch (err) {
      toast.error(apiError(err)); nav("/matches");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const memberName = (mid) => members.find((x) => x.id === mid)?.name || "—";

  const remove = async () => {
    if (!window.confirm("Delete this match? Expenses will be reversed.")) return;
    try {
      await api.delete(`/matches/${id}`);
      toast.success("Match deleted");
      nav("/matches");
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!match) {
    return (
      <div className="pb-24 px-5 pt-6">
        <Skeleton className="h-8 w-40 mb-4" />
        <Skeleton className="h-32 mb-3" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  return (
    <div className="pb-28">
      <div className="flex items-center justify-between px-3 pt-4">
        <button onClick={() => nav(-1)} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
        {canManage && (
          <button onClick={remove} className="h-10 w-10 rounded-full hover:bg-red-50 text-red-500 grid place-items-center" data-testid="match-delete-btn">
            <Trash2 size={18} />
          </button>
        )}
      </div>

      <TopBar
        title={`vs ${match.opponent}`}
        subtitle={match.format}
      />

      <div className="px-5 space-y-4">
        <Card>
          <div className="grid grid-cols-2 gap-4">
            <Info icon={Calendar} label="Date" value={fmtDate(match.date)} />
            <Info icon={MapPin} label="Ground" value={match.ground} />
            <Info icon={Users} label="Playing XI" value={(match.playing_xi || []).length} />
            <Info icon={Receipt} label="Total spent" value={inr(match.total_expense || 0)} />
          </div>
          {match.notes && (
            <div className="mt-4 pt-4 border-t border-black/[0.06] text-sm text-ink-muted">
              {match.notes}
            </div>
          )}
        </Card>

        <Button variant="dark" className="w-full" onClick={() => nav(`/scores/new?match=${id}`)} data-testid="match-score-btn">
          <Activity size={16}/> Score this match
        </Button>

        <div>
          <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-2">
            Playing XI
          </div>
          {(match.playing_xi || []).length === 0 ? (
            <Card className="p-0"><Empty icon={Users} title="No players selected" /></Card>
          ) : (
            <Card className="p-2">
              <div className="grid grid-cols-2 gap-1.5">
                {match.playing_xi.map((mid) => (
                  <div key={mid} className="px-3 py-2 rounded-xl bg-black/[0.03] text-sm font-semibold truncate">
                    {memberName(mid)}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold">
              Expenses
            </div>
            {canManage && (
              <button
                onClick={() => nav(`/expenses/new?match=${id}`)}
                className="text-xs font-semibold text-brand flex items-center gap-1"
                data-testid="add-match-expense-btn"
              >
                <PlusCircle size={13} /> Add
              </button>
            )}
          </div>
          {(match.expenses || []).length === 0 ? (
            <Card className="p-0"><Empty icon={Receipt} title="No expenses yet" subtitle="Add ground rent, ball etc." /></Card>
          ) : (
            <Card className="p-2 divide-y divide-black/[0.06]">
              {match.expenses.map((e) => (
                <div key={e.id} className="flex items-center gap-3 px-3 py-3">
                  <div className="h-9 w-9 rounded-2xl bg-brand-50 text-brand grid place-items-center">
                    <Receipt size={16}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{e.description || e.category}</div>
                    <div className="text-xs text-ink-muted">
                      Paid by {memberName(e.paid_by)} • {(e.included_members||[]).length} shares
                    </div>
                  </div>
                  <div className="font-bold">{inr(e.amount)}</div>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-ink-muted font-semibold flex items-center gap-1">
        <Icon size={12}/> {label}
      </div>
      <div className="text-base font-bold mt-1 truncate">{value}</div>
    </div>
  );
}
